import type { OutOfStockItem, ProductCart } from '@models';

/**
 * RCP-458 — copy for the available-count limits. There are no designs, so these reuse the existing
 * error surfaces (tile alert, top snackbar, dialog). None of them takes the remaining count: it is
 * never shown to the player (DoD13).
 */
export const STOCK_MESSAGES = {
  /** DoD10 — top snackbar when an add / quantity raise goes beyond the available count. */
  snackbarTitle: 'Not enough stock',
  addOrRaise: (names: string): string => `${names} isn't available in the quantity you selected.`,
  /** DoD10 — PDP note under the disabled "Add to cart"; the product's name is already the page title. */
  pdpLimitReached: "This item isn't available in the quantity you selected.",

  /** DoD11 — cart tile whose quantity is above the available count. */
  tileExceeds: 'This quantity is no longer available. Please reduce it to continue to checkout.',

  /** DoD11 / DoD12 — checkout blocked, naming each failing product. */
  blockedTitle: 'Some items are unavailable',
  blockedList: (names: string): string =>
    `These items are out of stock or not available in the requested quantity: ${names}. ` +
    'Please update your cart to continue.',

  /** DoD12 — "Place order" rejected by the authoritative stock check. */
  orderDialogTitle: 'Order not placed',
  orderDialogPrimary: 'Go to cart',
  orderDialogSecondary: 'Return to Homepage',

  /** Fallback when the backend reports a product it no longer knows (empty `name`). */
  unknownName: 'One of the items',
} as const;

/**
 * RCP-458 — the names of the failing products, comma-separated and de-duplicated. An empty backend
 * `name` falls back to the cart's own name for that product, then to a neutral phrase.
 */
export const stockProductNames = (
  items: Pick<OutOfStockItem, 'productId' | 'name'>[],
  cartProducts: Pick<ProductCart, 'productId' | 'name'>[] = [],
): string => {
  const names = items.map(
    (item) =>
      item.name ||
      cartProducts.find((product) => product.productId === item.productId)?.name ||
      STOCK_MESSAGES.unknownName,
  );
  return [...new Set(names)].join(', ');
};
